import {
	type AssistantStyleDto,
	type ExtraRuleDto,
	isExpressiveFrequency,
	isStylePreset,
	isWaitSoundVolume,
	STYLE_LIMITS,
	type StylePreset,
} from "@/types/ai-assistant-style";

/**
 * Logika "Gaya & aturan Jenna" (#59) — dipakai form admin dan server supaya
 * aturannya sama. Error dikembalikan sebagai kode; teks per bahasa ada di
 * `src/locales/assistant-style.ts` (server memakai teks Indonesia).
 */

export type StyleErrorCode =
	| "preset"
	| "frequency"
	| "waitVolume"
	| "wordsMax"
	| "wordChars"
	| "rulesMax"
	| "ruleEmpty"
	| "ruleChars"
	| "waitPhrasesMin"
	| "waitPhrasesMax"
	| "waitPhraseChars";

/** Kunci: `chatStyle`, `voiceStyle`, `waitSoundVolume`, `expressiveFrequency`, `expressiveWords`, `voiceWaitPhrases`, `extraRules`, `extraRules.<i>.larangan|pengganti`. */
export type StyleErrors = Record<string, StyleErrorCode>;

const L = STYLE_LIMITS;

/** Trim, rapikan spasi, buang kosong & duplikat (tanpa beda huruf besar). */
function uniqueWords(words: readonly string[]): string[] {
	const seen = new Set<string>();
	return words
		.map((w) => w.trim().replace(/\s+/g, " "))
		.filter((w) => {
			const key = w.toLocaleLowerCase("id-ID");
			if (!w || seen.has(key)) return false;
			seen.add(key);
			return true;
		});
}

/** Trim kata, kalimat tunggu & larangan; yang kosong atau duplikat dibuang. */
export function normalizeStyleSettings(
	s: AssistantStyleDto,
): AssistantStyleDto {
	const expressiveWords = uniqueWords(s.expressiveWords);
	const voiceWaitPhrases = uniqueWords(s.voiceWaitPhrases);
	const extraRules: ExtraRuleDto[] = s.extraRules.map((r) => ({
		larangan: r.larangan.trim(),
		pengganti: r.pengganti.trim(),
		aktif: r.aktif,
	}));
	return { ...s, expressiveWords, voiceWaitPhrases, extraRules };
}

function ruleFieldError(value: string): StyleErrorCode | null {
	if (!value) return "ruleEmpty";
	if (value.length > L.extraRuleCharsMax) return "ruleChars";
	return null;
}

/** Error per field untuk nilai yang SUDAH dinormalisasi; objek kosong = valid. */
export function validateStyleSettings(s: AssistantStyleDto): StyleErrors {
	const errors: StyleErrors = {};
	if (!isStylePreset(s.chatStyle)) errors.chatStyle = "preset";
	if (!isStylePreset(s.voiceStyle)) errors.voiceStyle = "preset";
	if (!isExpressiveFrequency(s.expressiveFrequency))
		errors.expressiveFrequency = "frequency";
	if (!isWaitSoundVolume(s.waitSoundVolume))
		errors.waitSoundVolume = "waitVolume";
	if (s.expressiveWords.length > L.expressiveWordsMax)
		errors.expressiveWords = "wordsMax";
	else if (s.expressiveWords.some((w) => w.length > L.expressiveWordCharsMax))
		errors.expressiveWords = "wordChars";
	if (s.voiceWaitPhrases.length < L.waitPhrasesMin)
		errors.voiceWaitPhrases = "waitPhrasesMin";
	else if (s.voiceWaitPhrases.length > L.waitPhrasesMax)
		errors.voiceWaitPhrases = "waitPhrasesMax";
	else if (s.voiceWaitPhrases.some((w) => w.length > L.waitPhraseCharsMax))
		errors.voiceWaitPhrases = "waitPhraseChars";
	if (s.extraRules.length > L.extraRulesMax) errors.extraRules = "rulesMax";
	s.extraRules.forEach((r, i) => {
		for (const field of ["larangan", "pengganti"] as const) {
			const code = ruleFieldError(r[field]);
			if (code) errors[`extraRules.${i}.${field}`] = code;
		}
	});
	return errors;
}

type PreviewLang = "id" | "en";

/** Kalimat contoh — angka tetap di kalimat sendiri, terpisah dari kata ekspresif (aturan #59). */
const PREVIEW_TEMPLATES: Record<PreviewLang, Record<StylePreset, string>> = {
	id: {
		formal:
			"Baik, Bapak/Ibu. Total APBDes 2026 sekitar 940 juta rupiah dan realisasinya sudah mencapai 62%.",
		ramah:
			"Total APBDes 2026 sekitar 940 juta rupiah, dan realisasinya sudah 62%.",
		santai:
			"Total APBDes 2026 sekitar 940 juta rupiah, realisasinya sudah 62% nih.",
	},
	en: {
		formal:
			"Certainly. The total 2026 village budget is about 940 million rupiah, and 62% has been realized.",
		ramah:
			"The total 2026 village budget is about 940 million rupiah, and 62% has been realized.",
		santai:
			"The 2026 village budget is about 940 million rupiah, and 62% is already spent.",
	},
};

/** Pratinjau statis (tanpa panggilan AI): kata ekspresif pertama + kalimat contoh preset. */
export function buildStylePreview(
	preset: StylePreset,
	words: readonly string[],
	lang: PreviewLang,
): string {
	const base = PREVIEW_TEMPLATES[lang][preset];
	const word = preset === "formal" ? "" : (words[0]?.trim() ?? "");
	if (!word) return base;
	const opener = /[.!?…]$/u.test(word) ? word : `${word}.`;
	return `${opener} ${base}`;
}
